import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useCart } from '@/context/CartContext';
import { Button } from '@/components/ui/button';
import { X, Minus, Plus, Trash2, ShoppingBag, ArrowRight } from 'lucide-react';

export const CartDrawer = () => {
  const navigate = useNavigate();
  const { 
    items, 
    itemCount, 
    subtotal, 
    hasInquiryItems,
    isCartOpen, 
    closeCart, 
    removeItem, 
    updateQuantity,
    clearCart 
  } = useCart();
  
  const [isClosing, setIsClosing] = useState(false);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      closeCart();
      setIsClosing(false);
    }, 250);
  };

  const handleCheckout = () => {
    handleClose();
    setTimeout(() => {
      navigate('/checkout');
    }, 300);
  };

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (isCartOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isCartOpen]);

  // Close on escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isCartOpen) {
        handleClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isCartOpen]);

  if (!isCartOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div 
        className={cn(
          "fixed inset-0 z-50 bg-foreground/60 backdrop-blur-sm",
          "transition-opacity duration-250",
          isClosing ? "opacity-0" : "opacity-100 animate-fade-in"
        )}
        onClick={handleClose}
        aria-hidden="true"
      />
      
      {/* Drawer */}
      <div 
        className={cn(
          "fixed right-0 top-0 bottom-0 z-50 w-full sm:w-[450px] bg-card shadow-elevated",
          "flex flex-col",
          "transition-transform duration-300 ease-out",
          isClosing ? "translate-x-full" : "translate-x-0 animate-slide-in-right"
        )}
        role="dialog"
        aria-modal="true"
        aria-labelledby="cart-title"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <ShoppingBag className="w-6 h-6 text-foreground" />
            <h2 id="cart-title" className="text-xl font-display text-foreground">
              Your Cart
            </h2>
            {itemCount > 0 && (
              <span className="px-2 py-0.5 text-sm bg-secondary text-secondary-foreground rounded-full">
                {itemCount} {itemCount === 1 ? 'item' : 'items'}
              </span>
            )}
          </div>
          <button
            onClick={handleClose}
            className="touch-target rounded-full hover:bg-secondary transition-colors focus-gallery"
            aria-label="Close cart"
          >
            <X className="w-6 h-6 text-foreground" />
          </button>
        </div>
        
        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto scrollbar-gallery p-6">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <ShoppingBag className="w-16 h-16 text-muted-foreground/30 mb-4" />
              <p className="text-lg text-muted-foreground mb-2">Your cart is empty</p>
              <p className="text-sm text-muted-foreground">
                Add some beautiful art to get started
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {items.map((item) => (
                <div 
                  key={item.itemKey}
                  className="flex gap-4 pb-6 border-b border-border last:border-0"
                >
                  {/* Product Image */}
                  <div className="w-20 h-20 sm:w-24 sm:h-24 flex-shrink-0 bg-muted rounded-sm overflow-hidden">
                    <img
                      src={item.selectedImage || item.product.image}
                      alt={item.product.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  
                  {/* Product Details */}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-display text-foreground text-base sm:text-lg truncate">
                      {item.product.title}
                    </h3>
                    {item.selectedSize && (
                      <span className="inline-block mt-0.5 text-xs text-muted-foreground uppercase tracking-wider" data-testid="cart-item-size">
                        Size {item.selectedSize}
                      </span>
                    )}
                    {item.dedication && (
                      <p className="mt-1 text-xs text-muted-foreground italic border-l-2 border-accent pl-2 line-clamp-2" data-testid="cart-item-dedication">
                        “{item.dedication}”
                      </p>
                    )}
                    {item.note && (
                      <p className="mt-1 text-xs text-muted-foreground border-l-2 border-accent pl-2 line-clamp-3" data-testid="cart-item-note">
                        {item.note}
                      </p>
                    )}
                    {item.product.badge && (
                      <span className="block text-xs text-muted-foreground uppercase tracking-wider">
                        {item.product.badge}
                      </span>
                    )}
                    <p className={cn(
                      "text-base font-medium mt-1",
                      item.product.price !== null ? "text-foreground" : "text-accent italic"
                    )}>
                      {(() => {
                        const p = item.unitPrice ?? item.product.price;
                        if (p !== null && p !== undefined) return `$${p}`;
                        return item.product.priceLabel || "Inquire";
                      })()}
                    </p>
                    
                    {/* Quantity Controls */}
                    <div className="flex items-center gap-3 mt-3">
                      <div className="flex items-center border border-border rounded-sm">
                        <button
                          onClick={() => updateQuantity(item.itemKey, item.quantity - 1)}
                          className="p-2 hover:bg-secondary transition-colors"
                          aria-label="Decrease quantity"
                        >
                          <Minus className="w-4 h-4 text-foreground" />
                        </button>
                        <span className="w-10 text-center text-sm font-medium text-foreground">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => updateQuantity(item.itemKey, item.quantity + 1)}
                          className="p-2 hover:bg-secondary transition-colors"
                          aria-label="Increase quantity"
                        >
                          <Plus className="w-4 h-4 text-foreground" />
                        </button>
                      </div>
                      
                      <button
                        onClick={() => removeItem(item.itemKey)}
                        className="p-2 text-muted-foreground hover:text-destructive transition-colors"
                        aria-label="Remove item"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Footer */}
        {items.length > 0 && (
          <div className="p-6 border-t border-border bg-background-subtle">
            {/* Subtotal */}
            <div className="flex items-center justify-between mb-4">
              <span className="text-muted-foreground">Subtotal</span>
              <span className="text-2xl font-medium text-foreground">
                ${subtotal.toFixed(2)}
              </span>
            </div>
            
            {/* Inquiry Notice */}
            {hasInquiryItems && (
              <p className="text-sm text-muted-foreground mb-4 text-center">
                * Some items require inquiry for pricing
              </p>
            )}
            
            {/* Checkout Button */}
            <Button
              onClick={handleCheckout}
              className="w-full btn-purchase text-lg py-6 group"
              size="lg"
            >
              Proceed to Checkout
              <ArrowRight className="w-5 h-5 ml-2 opacity-60 group-hover:opacity-100 transition-opacity" />
            </Button>
            
            {/* Clear Cart */}
            <button
              onClick={clearCart}
              className="w-full mt-3 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Clear Cart
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default CartDrawer;
